<template>
  <form novalidate @submit.prevent="submitQuote">
    <LsSelect
      v-model="bushes_0ft_5ft_count"
      :options="options"
      label="How many shrubs and hedges that are less than 5 ft tall need to be trimmed?"
      track-id="onboarding_instant_quote_bush_trimming_form-bushes_0ft_5ft_count"
      name="bushes_0ft_5ft_count"
      test-id="bushes_0ft_5ft_count"
      :disabled="disableInput"
      :validation="{ required: true, numeric: true }"
    />
    <LsSelect
      v-model="bushes_5ft_10ft_count"
      :options="options"
      label="How many shrubs and hedges that are between 5 ft to 10 ft tall need to be trimmed?"
      collapse-content="Medium price impact on total quote amount. Trimming shrubs or hedges of this size might require specialized tools."
      track-id="onboarding_instant_quote_bush_trimming_form-bushes_5ft_10ft_count"
      name="bushes_5ft_10ft_count"
      test-id="bushes_5ft_10ft_count"
      :disabled="disableInput"
      :validation="{ required: true, numeric: true }"
    />
    <LsSelect
      v-model="bushes_over_10ft_count"
      :options="options"
      label="How many shrubs and hedges that are more than 10 ft tall need to be trimmed?"
      collapse-content="High price impact on total quote amount. Trimming shrubs or hedges of this size might require a significant amount of time and specialized tools."
      track-id="onboarding_instant_quote_bush_trimming_form-bushes_over_10ft_count"
      name="bushes_over_10ft_count"
      test-id="bushes_over_10ft_count"
      :disabled="disableInput"
      :validation="{ required: true, numeric: true }"
    />
    <LsRadios
      v-model="desired_clippings_haulaway"
      :options="[{ label: 'Yes', value: true }, { label: 'No', value: false }]"
      name="desired_clippings_haulaway"
      :validation="{ required: true }"
      label="Would you like the provider to bag and haul off the clippings?"
      collapse-content="Medium price impact on total quote amount as the provider has to haul the clippings to a specific location and may have to pay a disposal fee."
      track-id="onboarding_instant_quote_bush_trimming_form-desired_clippings_haulaway"
      :disabled="disableInput"
    />
    <LsCheckboxes
      v-model="locations"
      :options="InstantQuoteBushTrimmingConstants.locations"
      label="Where on the property? (From the street)"
      track-id="onboarding_instant_quote_bush_trimming_form-locations"
      name="locations"
      test-id="locations"
      :validation="{ required: true }"
      :disabled="disableInput"
    />
    <LsRadios
      v-if="showCycleOptions"
      v-model="cycle"
      :options="InstantQuoteConstants.cycleOptions"
      name="cycle"
      :validation="{ required: true }"
      label="How frequently would you like to get your shrubs and hedges trimmed?"
      track-id="onboarding_instant_quote_bush_trimming_form-cycle"
      :disabled="disableInput"
    />
    <LsInput
      v-model="details"
      track-id="onboarding_instant_quote_bush_trimming_form-details"
      test-id="details"
      label="Any other details you would like your lawn care pro to know?"
      name="details"
      type="textarea"
      placeholder="Add as much detail as possible so that the provider can complete the job as per your expectations."
      :disabled="disableInput"
    />
    <LsUploadPhotos
      v-model="images"
      track-id="onboarding_instant_quote_bush_trimming_form-images"
      test-id="images"
      label="Upload photos"
      collapse-content="Upload photos so that your provider knows what shrubs/hedges need to be trimmed and what tools they would need to complete the job."
      name="images"
      :disabled="disableInput"
      :is-getting-images-from-order="isGettingImagesFromOrder"
    />
  </form>
</template>

<script>
import _ from 'lodash';
import { LsRadios, LsInput, LsUploadPhotos, LsCheckboxes, LsSelect } from '@/components/common';
import { mapActions, mapState } from 'vuex';
import { ONBOARDING__UPDATE_INSTANT_QUOTE } from '@/store/types';
import { lsInstantQuoteFormHelpers, lsUploadPhotosHelper } from '@/helpers';
import { lsValidationService, lsPropTypeHelpers } from 'ls-vue-common';
import { InstantQuoteConstants, InstantQuoteBushTrimmingConstants } from 'ls-constants';
import VueTypes from 'vue-types';

export default {
  name: 'OnboardingInstantQuoteBushTrimmingForm',
  $_veeValidate: {
    validator: 'new',
  },
  components: {
    LsRadios,
    LsInput,
    LsUploadPhotos,
    LsCheckboxes,
    LsSelect,
  },
  props: {
    currentInstantQuote: VueTypes.shape({
      id: VueTypes.number,
      instant_quote_bush_trimming: VueTypes.shape({
        bushes_0ft_5ft_count: VueTypes.custom(lsPropTypeHelpers.numberOrNull),
        bushes_5ft_10ft_count: VueTypes.custom(lsPropTypeHelpers.numberOrNull),
        bushes_over_10ft_count: VueTypes.custom(lsPropTypeHelpers.numberOrNull),
        bushes_property_location_back: VueTypes.custom(lsPropTypeHelpers.booleanOrNull),
        bushes_property_location_front: VueTypes.custom(lsPropTypeHelpers.booleanOrNull),
        bushes_property_location_full: VueTypes.custom(lsPropTypeHelpers.booleanOrNull),
        bushes_property_location_left: VueTypes.custom(lsPropTypeHelpers.booleanOrNull),
        bushes_property_location_right: VueTypes.custom(lsPropTypeHelpers.booleanOrNull),
        desired_trim_length: VueTypes.custom(lsPropTypeHelpers.stringOrNull),
        details: VueTypes.custom(lsPropTypeHelpers.stringOrNull),
        cycle: VueTypes.custom(lsPropTypeHelpers.numberOrNull),
        desired_clippings_haulaway: VueTypes.custom(lsPropTypeHelpers.booleanOrNull),
        images: VueTypes.array,
      }).loose,
    }).loose.isRequired,
  },
  data() {
    const { cycleOptions } = InstantQuoteConstants;
    return {
      isGettingImagesFromOrder: false,
      // form
      bushes_0ft_5ft_count: null,
      bushes_5ft_10ft_count: null,
      bushes_over_10ft_count: null,
      cycle: lsInstantQuoteFormHelpers.getDefaultCycleValue({ cycleOptions }),
      desired_trim_length: 'neat',
      details: '',
      desired_clippings_haulaway: false,
      images: [],
      locations: [],
    };
  },
  computed: {
    ...mapState(['featureFlags']),
    InstantQuoteConstants() {
      return _.cloneDeep(InstantQuoteConstants);
    },
    InstantQuoteBushTrimmingConstants() {
      return _.cloneDeep(InstantQuoteBushTrimmingConstants);
    },
    options() {
      return _.map(_.range(31), (num) => {
        return { label: _.toString(num), value: num };
      });
    },
    disableInput() {
      return this.currentInstantQuote.instant_quote_status !== 'customer.info';
    },
    showCycleOptions() {
      return _.get(this.featureFlags.flags, ['customer.instant_quotes.frequency_options']);
    },
  },
  created() {
    const { instant_quote_bush_trimming } = this.currentInstantQuote;
    const existingQuote = _.cloneDeep(instant_quote_bush_trimming);

    const {
      bushes_0ft_5ft_count,
      bushes_5ft_10ft_count,
      bushes_over_10ft_count,
      cycle,
      desired_trim_length,
      details,
      desired_clippings_haulaway,
      images,
    } = existingQuote;

    if (_.size(images)) {
      this.isGettingImagesFromOrder = true;
    }

    const locations = [];
    _.forEach(instant_quote_bush_trimming, (value, key) => {
      if (_.includes(key, 'bushes_property_location') && value) {
        locations.push(key);
      }
    });

    const isUntouchedForm = Object.entries(instant_quote_bush_trimming).some(([key, value]) => {
      return (key.includes('bush_') || key.includes('bushes_')) && !value;
    });

    if (!isUntouchedForm) {
      this.cycle = cycle;
    }

    this.bushes_0ft_5ft_count = bushes_0ft_5ft_count;
    this.bushes_5ft_10ft_count = bushes_5ft_10ft_count;
    this.bushes_over_10ft_count = bushes_over_10ft_count;
    this.desired_trim_length = desired_trim_length ? desired_trim_length : 'neat';
    this.details = details ? details : '';
    this.desired_clippings_haulaway = desired_clippings_haulaway
      ? desired_clippings_haulaway
      : false;
    this.images = images;
    this.locations = locations;
  },
  methods: {
    ...mapActions([ONBOARDING__UPDATE_INSTANT_QUOTE]),
    async generateInstantQuote() {
      const isFormValid = await this.$validator.validateAll();
      const isUploadValid = lsUploadPhotosHelper.isValidUpload(this.images);

      if (!isFormValid || !isUploadValid) {
        return lsValidationService.scrollToInvalid();
      }

      await this[ONBOARDING__UPDATE_INSTANT_QUOTE]({
        instantQuotableId: this.currentInstantQuote.instant_quotable_id,
        formData: {
          bushes_0ft_5ft_count: this.bushes_0ft_5ft_count,
          bushes_5ft_10ft_count: this.bushes_5ft_10ft_count,
          bushes_over_10ft_count: this.bushes_over_10ft_count,
          cycle: this.cycle,
          desired_trim_length: this.desired_trim_length,
          details: this.details ? this.details : null,
          desired_clippings_haulaway: this.desired_clippings_haulaway,
          images: this.images,
          locations: this.locations,
        },
        type: 'instant_quote_bush_trimming',
      });

      lsInstantQuoteFormHelpers.scrollToBottom({ document });
    },
  },
};
</script>

<style lang="scss" scoped>
form > :not(:last-child) {
  margin-bottom: 20px !important;
}
</style>
